import { useMemo, useRef, useState } from 'react'
import { motion, type PanInfo } from 'framer-motion'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import { useTimeline } from '../../context/useTimeline'
import type { TimelineElement } from '../../context/TimelineContext'
import { useNavigation } from '../../context/useNavigation'
import { Button, IconButton } from '../Common'
import { CanvasOverlay } from '../Canvas/CanvasOverlay'
import type { DashboardViewProps } from '../DashboardContainer'

/**
 * War Room Blueprint View — infinite 2D pan/zoom canvas projecting tasks &
 * subtasks as circular "table nodes" at spatial coordinates.
 *
 * Spatial coords live on TimelineElement as optional `spatialX/spatialY/spatialZone`.
 * Elements without persisted coords get a deterministic Fibonacci-spiral
 * fallback so the first open shows a clean centered cluster rather than a
 * pile at one fallback point. Once the planner drags a node, its real coords
 * persist via `updateElement` and the fallback is no longer used for that id.
 *
 * Not wired into DashboardContainer's switcher this phase — standalone file.
 */

const CANVAS_SIZE = 3000
const CANVAS_CENTER = CANVAS_SIZE / 2
const NODE_SIZE = 80 // matches w-20 / h-20
const NODE_RADIUS = NODE_SIZE / 2
const MIN_SCALE = 0.5
const MAX_SCALE = 2.0
const SCALE_STEP = 0.1
const DRAG_PERSIST_THRESHOLD_PX = 5

const SPIRAL_GOLDEN_ANGLE = 2.39996 // rad — Vogel's sunflower phyllotaxis
const SPIRAL_SPACING_RADIUS = 120

const DRAGGABLE_TYPES: ReadonlyArray<TimelineElement['type']> = ['TASK', 'SUBTASK']

const DEFAULT_ZONE = 'Main Floor'

interface PlottedNode {
  element: TimelineElement
  x: number
  y: number
  zone: string
}

/** Vogel's sunflower spiral — index → (x, y) around the canvas center. */
const fibonacciFallback = (index: number): { x: number; y: number } => {
  const angle = index * SPIRAL_GOLDEN_ANGLE
  const radius = SPIRAL_SPACING_RADIUS * Math.sqrt(index)
  return {
    x: CANVAS_CENTER + radius * Math.cos(angle),
    y: CANVAS_CENTER + radius * Math.sin(angle),
  }
}

const clampToCanvas = (value: number): number =>
  Math.max(NODE_RADIUS, Math.min(CANVAS_SIZE - NODE_RADIUS, value))

const clampScale = (value: number): number =>
  Math.max(MIN_SCALE, Math.min(MAX_SCALE, value))

const isInNavigationBranch = (
  element: TimelineElement,
  elementsById: ReadonlyMap<string, TimelineElement>,
  drillGoalId: string | null,
  drillTaskId: string | null,
) => {
  if (!drillGoalId) return true

  if (drillTaskId) {
    return element.type === 'SUBTASK' && element.parentId === drillTaskId
  }

  if (element.type === 'TASK') return element.parentId === drillGoalId

  if (element.type === 'SUBTASK' && element.parentId) {
    const parentTask = elementsById.get(element.parentId)
    return parentTask?.parentId === drillGoalId
  }

  return false
}

/**
 * Pre-compute the centered (cx, cy) coordinate for every draggable element.
 * Unplaced items get spiral fallbacks; placed items use their persisted coords.
 * Splitting them by placement before indexing keeps the spiral pattern stable
 * as soon as items start getting real coords.
 */
const buildPlots = (elements: TimelineElement[]): PlottedNode[] => {
  const draggable = elements.filter((e) => DRAGGABLE_TYPES.includes(e.type))
  const unplaced: TimelineElement[] = []
  const plots: PlottedNode[] = []

  for (const el of draggable) {
    if (typeof el.spatialX === 'number' && typeof el.spatialY === 'number') {
      plots.push({
        element: el,
        x: clampToCanvas(el.spatialX),
        y: clampToCanvas(el.spatialY),
        zone: el.spatialZone ?? DEFAULT_ZONE,
      })
    } else {
      unplaced.push(el)
    }
  }
  unplaced.forEach((el, index) => {
    const { x, y } = fibonacciFallback(index)
    plots.push({
      element: el,
      x: clampToCanvas(x),
      y: clampToCanvas(y),
      zone: el.spatialZone ?? DEFAULT_ZONE,
    })
  })
  return plots
}

interface TableNodeProps {
  plot: PlottedNode
  scale: number
  onPersist: (id: string, x: number, y: number) => void
  onOpenDetails: (element: TimelineElement) => void
}

const TableNode = ({ plot, scale, onPersist, onOpenDetails }: TableNodeProps) => {
  const { element, x, y } = plot
  const percent = Math.max(0, Math.min(100, Math.round(element.progress ?? 0)))
  const isDone = percent >= 100

  // SVG progress ring math — 36px radius gives a circumference of ~226.
  const ringRadius = 36
  const circumference = 2 * Math.PI * ringRadius
  const ringOffset = circumference * (1 - percent / 100)

  // Ref to recover the final position after drag — framer-motion drag updates
  // transform; we read the DOM rect to back out the absolute canvas coords
  // (which already accounts for any pointer constraints applied by the lib).
  const nodeRef = useRef<HTMLDivElement>(null)

  const handleDragEnd = (_e: unknown, info: PanInfo) => {
    const dx = info.offset.x / scale
    const dy = info.offset.y / scale
    if (Math.abs(dx) < DRAG_PERSIST_THRESHOLD_PX && Math.abs(dy) < DRAG_PERSIST_THRESHOLD_PX) return
    const nextX = clampToCanvas(x + dx)
    const nextY = clampToCanvas(y + dy)
    onPersist(element.id, nextX, nextY)
  }

  return (
    <motion.div
      ref={nodeRef}
      drag
      dragMomentum={false}
      dragConstraints={{
        left: NODE_RADIUS - x,
        right: CANVAS_SIZE - NODE_RADIUS - x,
        top: NODE_RADIUS - y,
        bottom: CANVAS_SIZE - NODE_RADIUS - y,
      }}
      whileDrag={{ scale: 1.1, zIndex: 40, cursor: 'grabbing' }}
      onDragEnd={handleDragEnd}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={() => onOpenDetails(element)}
      style={{ top: y - NODE_RADIUS, left: x - NODE_RADIUS }}
      className="absolute w-20 h-20 rounded-full flex flex-col items-center justify-center cursor-pointer select-none transition-all duration-150"
      title={`${element.title}${plot.zone ? ` · ${plot.zone}` : ''} · ${percent}%`}
    >
      {/* SVG progress ring — sits behind the inner content via absolute. */}
      <svg
        className="absolute inset-0 -rotate-90"
        viewBox="0 0 80 80"
        aria-hidden="true"
      >
        <circle
          cx="40"
          cy="40"
          r={ringRadius}
          style={{ fill: 'var(--surface-muted)', stroke: 'var(--border)' }}
          strokeWidth="2"
        />
        <circle
          cx="40"
          cy="40"
          r={ringRadius}
          fill="none"
          style={{ stroke: isDone ? 'var(--success)' : 'var(--accent)' }}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={isDone ? 0 : ringOffset}
        />
      </svg>

      <div className="relative z-10 flex flex-col items-center justify-center px-2 text-center pointer-events-none">
        {isDone ? (
          <span className="text-[var(--success)] text-base leading-none mb-0.5" aria-hidden="true" /* mosaic-keep: icon */>✓</span>
        ) : (
          <span className="text-[8px] uppercase tracking-wider text-[var(--muted)] leading-none mb-0.5">
            {element.type}
          </span>
        )}
        <span className="text-[10px] font-semibold text-[var(--text-strong)] leading-tight line-clamp-2 break-words max-w-[64px]">
          {element.title}
        </span>
        {!isDone && (
          <span className="text-[9px] text-[color-mix(in_srgb,var(--accent)_60%,var(--text-strong))] font-medium tabular-nums mt-0.5">{percent}%</span>
        )}
      </div>
    </motion.div>
  )
}

export const WarRoomBlueprintView = ({ onElementClick, visualItems, activityRecords, isActivityLoading }: DashboardViewProps) => {
  const { elements: liveElements, updateElement } = useTimeline()
  const { activeGoalId: drillGoalId, activeTaskId: drillTaskId } = useNavigation()
  const [scale, setScale] = useState(1)
  const viewportRef = useRef<HTMLDivElement>(null)

  const elementsById = useMemo(() => new Map(liveElements.map((element) => [element.id, element])), [liveElements])
  const scopedElements = useMemo(
    () => liveElements
      .filter((element) => DRAGGABLE_TYPES.includes(element.type))
      .filter((element) => isInNavigationBranch(element, elementsById, drillGoalId, drillTaskId)),
    [drillGoalId, drillTaskId, elementsById, liveElements],
  )
  const plots = useMemo(() => buildPlots(scopedElements), [scopedElements])
  const highRiskItems = useMemo(
    () => visualItems.filter((item) => item.risk.severity >= 4),
    [visualItems],
  )

  // Aggregate the zone label most populated nodes claim — single-zone for now
  // (zone editor isn't part of this phase), so we show the dominant or default.
  const dominantZone = useMemo(() => {
    if (plots.length === 0) return DEFAULT_ZONE
    const counts = new Map<string, number>()
    for (const p of plots) counts.set(p.zone, (counts.get(p.zone) ?? 0) + 1)
    let best = DEFAULT_ZONE
    let bestCount = -1
    for (const [zone, count] of counts) {
      if (count > bestCount) {
        best = zone
        bestCount = count
      }
    }
    return best
  }, [plots])

  const handlePersist = (id: string, x: number, y: number) => {
    void updateElement(id, { spatialX: Math.round(x), spatialY: Math.round(y) }).catch((error) => {
      console.warn('[warroom] failed to persist spatial position', id, x, y, error)
    })
  }

  const zoomBy = (delta: number) => setScale((s) => clampScale(s + delta))

  return (
    <div
      ref={viewportRef}
      className="relative w-full h-full overflow-hidden bg-[var(--bg)]"
    >
      {/* ── Status overlay (top-left) ────────────────────────────────── */}
      <CanvasOverlay anchor="viewport">
        <div className="bg-[var(--surface-card)]/80 backdrop-blur text-xs font-semibold px-4 py-2 rounded-full absolute top-6 left-6 text-[var(--muted)] border border-[var(--border)] z-20">
          War Room Active - Zone: <span className="text-[var(--text-strong)]">{dominantZone}</span>
          {' · '}Plots: <span className="text-[var(--text-strong)]">{plots.length}</span>
          {' · '}Open risk: <span className="text-[var(--text-strong)]">{highRiskItems.length}</span>
          {' · '}Activity: <span className="text-[var(--text-strong)]">{isActivityLoading ? '...' : activityRecords.length}</span>
          {' · '}Zoom <span className="text-[var(--text-strong)]">{Math.round(scale * 100)}%</span>
        </div>
      </CanvasOverlay>

      <CanvasOverlay anchor="viewport">
        <aside className="absolute right-6 top-6 z-20 grid w-72 gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface-card)]/85 p-3 text-[var(--text)] shadow-2xl backdrop-blur">
          <div>
            <p className="m-0 text-[10px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">Tactical risk</p>
            <p className="m-0 mt-1 text-xs font-semibold text-[var(--muted)]">Signal count without checklist list rollups</p>
          </div>
          <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)]/70 px-3 py-3">
            <strong className="block text-lg font-black">{highRiskItems.length}</strong>
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">High-risk signals</span>
          </div>
          <div className="border-t border-[var(--border-subtle)] pt-2">
            <p className="m-0 text-[10px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">Live activity</p>
            <p className="m-0 mt-1 line-clamp-2 text-xs font-semibold text-[var(--text)]">
              {activityRecords[0]?.summary ?? 'No activity events in the current 30-day window.'}
            </p>
          </div>
        </aside>
      </CanvasOverlay>

      {plots.length === 0 && (
        <CanvasOverlay anchor="viewport">
          <div className="absolute inset-0 z-10 grid place-items-center text-center">
            <div className="max-w-sm rounded-xl border border-[var(--border)] bg-[var(--surface-card)]/85 p-5 text-[var(--text)] shadow-2xl backdrop-blur">
              <p className="m-0 text-sm font-black">No tactical plots yet</p>
              <p className="m-0 mt-2 text-xs font-semibold text-[var(--muted)]">
                Tasks and subtasks will appear here when the Dashboard has operational nodes.
              </p>
            </div>
          </div>
        </CanvasOverlay>
      )}

      {/* ── Zoom controls (bottom-left) ──────────────────────────────── */}
      <CanvasOverlay anchor="viewport" className="pannableCanvasOverlayInteractive">
        <div className="absolute bottom-6 left-6 z-20 flex gap-2">
          <IconButton
            variant="tertiary"
            size="sm"
            onClick={() => zoomBy(-SCALE_STEP)}
            disabled={scale <= MIN_SCALE + 1e-3}
            label="Zoom out"
            icon={<RemoveRoundedIcon fontSize="inherit" />}
          />
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setScale(1)}
            aria-label="Reset zoom"
          >
            {Math.round(scale * 100)}%
          </Button>
          <IconButton
            variant="tertiary"
            size="sm"
            onClick={() => zoomBy(SCALE_STEP)}
            disabled={scale >= MAX_SCALE - 1e-3}
            label="Zoom in"
            icon={<AddRoundedIcon fontSize="inherit" />}
          />
        </div>
      </CanvasOverlay>

      {/* ── Pannable canvas ─────────────────────────────────────────────
       *
       * Outer drag = pan the entire blueprint. Inner nodes have their own
       * drag — framer-motion's default dragPropagation:false means child
       * drag does NOT trigger parent drag, and the explicit
       * onPointerDown stopPropagation on TableNode is a belt-and-braces
       * guard for older runtimes.
       *
       * Scale is applied to the canvas itself (single `scale` transform)
       * so all child nodes zoom in unison without per-node math.
       */}
      <motion.div
        drag
        dragMomentum={false}
        style={{ scale }}
        className="bg-[var(--surface-muted)] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px] w-[3000px] h-[3000px] relative overflow-hidden cursor-grab active:cursor-grabbing origin-top-left" /* mosaic-keep: dot-grid pattern, not a glow */
      >
        {plots.map((plot) => (
          <TableNode
            key={plot.element.id}
            plot={plot}
            scale={scale}
            onPersist={handlePersist}
            onOpenDetails={onElementClick}
          />
        ))}
      </motion.div>
    </div>
  )
}
